import { useAuth } from '@clerk/react';
import { useLocalStorage } from '@mantine/hooks';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useDashboard } from './dashboard';
import { useMe, useTeam, useTeamIdContext } from './team';
import {
  getImpersonationActorContactId,
  getImpersonationActorTeamId,
} from './team/team.session.utils';
import {
  hasOwnerContactAdminAccess,
  isOwnerPreviewTeamSwitchPending,
  resolveOwnerPortalContactId,
} from './owner.utils';

type C = {
  contactId: string | null;
  setContactId: (value: string | null) => void;
  hasAdminAccess: boolean;
};

const ownerPortalContext = createContext<C>({
  contactId: null,
  setContactId: () => {},
  hasAdminAccess: false,
});

export type OwnerPortalPreviewSession = {
  contactId: string;
  teamId: string;
  exit: () => void;
};

/** Set inside a tab that renders the owner portal with a preview token. */
export const ownerPortalPreviewSessionContext =
  createContext<OwnerPortalPreviewSession | null>(null);

export const useOwnerPortalPreviewSession = () =>
  useContext(ownerPortalPreviewSessionContext);

/**
 * True while a manager previews the owner portal, either through a VRTrust
 * preview token or a Clerk owner impersonation. Owner portal writes stay
 * disabled in both.
 */
export const useIsOwnerPortalPreview = () => {
  const { actor } = useAuth();
  const previewSession = useOwnerPortalPreviewSession();

  return !!actor?.sub || !!previewSession;
};

export function useOwnerPortalContactIdLocalStorage() {
  const { id: meId } = useMe();

  const [contactId, setContactId] = useLocalStorage<string | null>({
    key: `${meId}_owp_cid`, // owp_cid = owner portal contact id
    defaultValue: null,
    getInitialValueInEffect: true,
  });

  return { contactId, setContactId };
}

export const OwnerPortalContactContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { id: meId, ownerAccess = [] } = useMe();
  const { actor } = useAuth();
  const [{ id: hydratedTeamId }] = useTeam();
  const { teamId: selectedTeamId } = useTeamIdContext();
  const teamScopeId = selectedTeamId ?? hydratedTeamId;

  const { contactId, setContactId } = useOwnerPortalContactIdLocalStorage();
  const [cti, setCti] = useQueryState('cti', parseAsString);
  const [sti] = useQueryState('sti', parseAsString);
  const queryContactId = getImpersonationActorContactId(actor) ?? cti;
  const queryTeamId = getImpersonationActorTeamId(actor) ?? sti;
  const isPreviewTeamSwitchPending = isOwnerPreviewTeamSwitchPending({
    queryTeamId,
    activeTeamId: teamScopeId,
  });

  const resolvedContactId = useMemo(
    () =>
      resolveOwnerPortalContactId({
        ownerAccess,
        teamId: teamScopeId,
        contactId,
        queryContactId,
      }),
    [ownerAccess, teamScopeId, contactId, queryContactId]
  );

  useEffect(() => {
    // Wait for user+owner access hydration before consuming an impersonation contact id.
    if (isPreviewTeamSwitchPending) return;
    if (!meId || !teamScopeId || !queryContactId || !ownerAccess.length) return;

    if (resolvedContactId === queryContactId && contactId !== queryContactId) {
      setContactId(queryContactId);
    }

    if (cti) {
      setCti(null);
    }
  }, [
    meId,
    teamScopeId,
    cti,
    queryContactId,
    ownerAccess.length,
    isPreviewTeamSwitchPending,
    resolvedContactId,
    contactId,
    setContactId,
    setCti,
  ]);

  const ownerPortalValue = useMemo<C>(
    () => ({
      contactId: resolvedContactId,
      setContactId,
      hasAdminAccess: hasOwnerContactAdminAccess({
        ownerAccess,
        teamId: teamScopeId,
        contactId: resolvedContactId,
      }),
    }),
    [ownerAccess, resolvedContactId, setContactId, teamScopeId]
  );

  return (
    <ownerPortalContext.Provider value={ownerPortalValue}>
      {children}
    </ownerPortalContext.Provider>
  );
};

export const useOwnerPortalContact = () => {
  const context = useContext(ownerPortalContext);

  const [dashboard] = useDashboard();
  const isOwnerPortal = dashboard === 'owner';

  return useMemo<typeof context>(() => {
    return {
      ...context,
      contactId: isOwnerPortal ? context.contactId : null,
      hasAdminAccess: isOwnerPortal ? context.hasAdminAccess : false,
    };
  }, [context, isOwnerPortal]);
};
